import React from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import DisplayUpdateTime from 'ui/DisplayUpdateTime'
import { DataTableRowActions } from './data-table-row-action'
import { Badge } from '@/components/ui/badge'
import type { TempSpineZip, TempSpineZipsTableMeta } from './types'
import {
  formatAutoDeleteDays,
  formatDaysUntilExpiry,
} from './expiry.utils'
import { UrlWithCopy } from 'ui/UrlWithCopy'

export const columns: ColumnDef<TempSpineZip>[] = [
  {
    header: '#',
    cell: ({ row }) => row.index + 1,
  },
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => (
      <span className="font-mono text-xs">{row.original.id}</span>
    ),
  },
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <div className="w-48">
        <p>{row.original.name}</p>
        {row.original.description ? (
          <p className="text-muted-foreground">{row.original.description}</p>
        ) : null}
      </div>
    ),
  },
  {
    accessorKey: 'zipUrl',
    header: 'Zip URL',
    cell: ({ row }) => <UrlWithCopy url={row.original.zipUrl} />,
  },
  {
    accessorKey: 'compressedZipUrl',
    header: 'Compressed Zip URL',
    cell: ({ row }) =>
      row.original.compressedZipUrl ? (
        <UrlWithCopy url={row.original.compressedZipUrl} />
      ) : (
        <span className="text-muted-foreground text-sm">—</span>
      ),
  },
  {
    accessorKey: 'autoDeleteDays',
    header: 'Auto delete (days)',
    cell: ({ row }) => (
      <Badge variant="outline">
        {formatAutoDeleteDays(row.original.autoDeleteDays)}
      </Badge>
    ),
  },
  {
    id: 'daysUntilExpiry',
    header: 'Time until expiry',
    cell: ({ row }) => {
      const label = formatDaysUntilExpiry(row.original.expiresAt)
      const isUrgent =
        label !== 'Permanent' && label !== 'Expired' && label !== '< 1 day left'

      return (
        <div className="flex flex-col gap-1">
          <Badge
            variant={
              label === 'Permanent'
                ? 'secondary'
                : label === 'Expired'
                  ? 'destructive'
                  : 'outline'
            }
          >
            {label}
          </Badge>
          {row.original.expiresAt && isUrgent ? (
            <span className="text-xs text-muted-foreground">
              <DisplayUpdateTime updateTime={row.original.expiresAt} />
            </span>
          ) : null}
        </div>
      )
    },
  },
  {
    accessorKey: 'expiresAt',
    header: 'Expires at',
    cell: ({ row }) =>
      row.original.expiresAt ? (
        <DisplayUpdateTime updateTime={row.original.expiresAt} />
      ) : (
        <span className="text-muted-foreground text-sm">—</span>
      ),
  },
  {
    header: 'UpdateTime',
    accessorKey: 'updateTime',
    cell: ({ row }) => (
      <DisplayUpdateTime updateTime={row.original.updateTime} />
    ),
  },
  {
    header: 'Actions',
    cell: ({ row, table }) => (
      <DataTableRowActions
        row={row}
        mutate={
          (table.options.meta as TempSpineZipsTableMeta | undefined)?.mutate
        }
      />
    ),
  },
]
